import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, from, firstValueFrom } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { getFirebaseBackend } from '../../authUtils';

/**
 * The token-refresh methods this interceptor was written against.
 *
 * `FirebaseAuthBackend` does not implement them. Until `_fireBaseBackend` was
 * given a type it was inferred as `null`, so every call here resolved to
 * `never` and the compiler said nothing — the calls would have thrown the
 * moment the backend was initialised (auth.service.ts calls
 * `initFirebaseBackend`). They are feature-detected below rather than assumed.
 *
 * Planning Center OIDC tokens are refreshed server-side, so in practice this
 * interceptor passes everything through. It is left registered rather than
 * removed because deleting a live HTTP interceptor is a bigger decision than a
 * type fix.
 */
interface TokenRefreshCapable {
    isTokenExpired(): boolean;
    refreshOidcToken(): Promise<string>;
    logout(): void;
}

/**
 * Whether the auth backend actually implements token refresh.
 *
 * @param {unknown} backend - The auth backend, if one has been initialised.
 * @return {boolean} True when the refresh methods are present.
 */
function canRefreshTokens(backend: unknown): backend is TokenRefreshCapable {
    const candidate = backend as Partial<TokenRefreshCapable> | null;
    return typeof candidate?.isTokenExpired === 'function' &&
        typeof candidate?.refreshOidcToken === 'function';
}

@Injectable()
export class TokenRefreshInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Only intercept Planning Center API requests
        if (!this.isPlanningCenterRequest(request)) {
            return next.handle(request);
        }

        const authBackend = getFirebaseBackend();
        if (!canRefreshTokens(authBackend)) {
            return next.handle(request);
        }

        // Check if token needs refresh before making the request
        if (authBackend.isTokenExpired()) {
            return this.handleTokenRefresh(request, next);
        }

        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                // If we get a 401 error, try to refresh the token
                if (error.status === 401 && this.isPlanningCenterRequest(request)) {
                    return this.handleTokenRefresh(request, next);
                }
                return throwError(error);
            })
        );
    }

    private isPlanningCenterRequest(request: HttpRequest<any>): boolean {
        return request.url.includes('api.planningcenteronline.com');
    }

    private handleTokenRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const authBackend = getFirebaseBackend();
        if (!canRefreshTokens(authBackend)) {
            return next.handle(request);
        }

        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            // from(...) because the caller needs an Observable; returning the
            // bare Promise never satisfied HttpInterceptor's contract.
            return from(authBackend.refreshOidcToken().then((newToken: string) => {
                this.isRefreshing = false;
                this.refreshTokenSubject.next(newToken);

                // Retry the original request with the new token
                const newRequest = this.addTokenToRequest(request, newToken);
                return firstValueFrom(next.handle(newRequest));
            }).catch((error) => {
                this.isRefreshing = false;
                console.error('Token refresh failed:', error);
                
                // Redirect to login if refresh fails
                authBackend.logout();
                window.location.href = '/auth/login';

                throw error;
            }));
        } else {
            // If a refresh is already in progress, wait for it to complete
            return this.refreshTokenSubject.pipe(
                filter(token => token !== null),
                take(1),
                switchMap((token: string) => {
                    const newRequest = this.addTokenToRequest(request, token);
                    return next.handle(newRequest);
                })
            );
        }
    }

    private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`
            }
        });
    }
}